import {
  type CSSProperties,
  type ReactElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ENGINE_MARKS, type EngineKey } from "./engine-config";
import { generateContainerPath } from "./geometry";
import { Glyph } from "./glyphs";
import { subscribeFrame } from "./scheduler";
import "./quiddity-mark.css";

export type MarkMotion = "auto" | "static";

export interface QuiddityMarkProps {
  readonly engine?: EngineKey;
  readonly size?: number | string;
  readonly motion?: MarkMotion;
  readonly decorative?: boolean;
  readonly label?: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

interface RingConfig {
  readonly rx: number;
  readonly ry: number;
  readonly speed: number;
  readonly phase: number;
  readonly strong?: boolean;
  readonly front?: boolean;
}

const RINGS: readonly RingConfig[] = [
  { rx: 76, ry: 70, speed: 0.18, phase: 0 },
  { rx: 93, ry: 87, speed: -0.12, phase: 0.8, strong: true },
  { rx: 111, ry: 101, speed: 0.08, phase: 1.5 },
  { rx: 126, ry: 116, speed: -0.22, phase: 2.2, front: true },
];

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  return reduced;
}

function dotTransform(radius: number, angleDegrees: number, scale = 1): string {
  const angle = (angleDegrees * Math.PI) / 180;
  const x = 150 + Math.cos(angle) * radius;
  const y = 150 + Math.sin(angle) * (radius * 0.82);
  return `translate(${x.toFixed(3)} ${y.toFixed(3)}) scale(${scale.toFixed(3)})`;
}

export function QuiddityMark({
  engine = "quiddity",
  size = "clamp(8rem, 28vw, 17.5rem)",
  motion = "auto",
  decorative = false,
  label,
  className = "",
  style,
}: QuiddityMarkProps): ReactElement {
  const config = ENGINE_MARKS[engine];
  const titleId = `quiddity-mark-${useId().replaceAll(":", "")}`;
  const accessibleLabel = label ?? `${config.label} — ${config.subtitle}`;
  const reducedMotion = usePrefersReducedMotion();
  const isStatic = motion === "static" || reducedMotion;
  const containerRef = useRef<SVGPathElement>(null);
  const glyphRef = useRef<SVGGElement>(null);
  const ringRefs = useRef<Array<SVGEllipseElement | null>>([]);
  const dotRefs = useRef<Array<SVGGElement | null>>([]);
  const initialPath = useMemo(
    () =>
      generateContainerPath({
        mode: config.container.modes[0],
        time: 0,
        baseRadius: config.glyph ? 54 : 58,
        morph: config.container.morph,
        wobble: config.container.wobble,
      }),
    [config],
  );

  useEffect(() => {
    const applyFrame = (elapsedSeconds: number) => {
      const modeIndex = Math.floor(
        elapsedSeconds / config.container.changeEvery,
      );
      const mode =
        config.container.modes[modeIndex % config.container.modes.length];
      const rotation =
        Math.sin(elapsedSeconds * 0.6) * config.container.rotation;

      containerRef.current?.setAttribute(
        "d",
        generateContainerPath({
          mode,
          time: elapsedSeconds,
          baseRadius: config.glyph ? 54 : 58,
          morph: config.container.morph,
          wobble: config.container.wobble,
        }),
      );
      containerRef.current?.setAttribute(
        "transform",
        `rotate(${rotation} 150 150)`,
      );
      glyphRef.current?.setAttribute(
        "transform",
        `rotate(${rotation * 0.12} 150 150)`,
      );

      const ringPulse = 1 + Math.sin(elapsedSeconds * 1.65) * 0.055;
      RINGS.forEach((ring, index) => {
        const element = ringRefs.current[index];
        if (!element) return;
        element.setAttribute("rx", (ring.rx * ringPulse).toFixed(3));
        element.setAttribute("ry", (ring.ry * ringPulse).toFixed(3));
        element.setAttribute(
          "opacity",
          (
            0.22 +
            ((Math.sin(elapsedSeconds * 1.65 + ring.phase) + 1) / 2) * 0.32
          ).toFixed(3),
        );
        element.setAttribute(
          "transform",
          `rotate(${elapsedSeconds * 30 * ring.speed} 150 150)`,
        );
      });

      config.dots.forEach((dot, index) => {
        const element = dotRefs.current[index];
        if (!element) return;
        const angle =
          dot.angle + elapsedSeconds * dot.speed * dot.direction * 360;
        const pulse = 1 + Math.sin(elapsedSeconds * 2 + index) * 0.12;
        element.setAttribute(
          "transform",
          dotTransform(dot.radius, angle, pulse),
        );
      });
    };

    applyFrame(0);
    if (isStatic) return undefined;

    const startedAt = performance.now();
    return subscribeFrame((now) => applyFrame((now - startedAt) / 1000));
  }, [config, isStatic]);

  const sizeValue = typeof size === "number" ? `${size}px` : size;
  const mergedStyle = {
    ...style,
    "--q-mark-size": sizeValue,
  } as CSSProperties;

  const renderDots = (front: boolean) =>
    config.dots.map((dot, index) => {
      if (dot.front !== front) return null;
      return (
        <g
          className="q-dot"
          key={`${front ? "front" : "back"}-${index}`}
          ref={(element) => {
            dotRefs.current[index] = element;
          }}
          transform={dotTransform(dot.radius, dot.angle)}
        >
          <circle fill={dot.color} r={dot.size} />
        </g>
      );
    });

  const renderRings = (front: boolean) =>
    RINGS.map((ring, index) => {
      if (Boolean(ring.front) !== front) return null;
      return (
        <ellipse
          className={`q-ring${ring.strong ? " q-ring--strong" : ""}`}
          cx="150"
          cy="150"
          key={`${front ? "front" : "back"}-${index}`}
          opacity={0.22 + ((Math.sin(ring.phase) + 1) / 2) * 0.32}
          ref={(element) => {
            ringRefs.current[index] = element;
          }}
          rx={ring.rx}
          ry={ring.ry}
        />
      );
    });

  return (
    <span className={`quiddity-mark ${className}`.trim()} style={mergedStyle}>
      <svg
        className="quiddity-mark__svg"
        viewBox="0 0 300 300"
        focusable="false"
        aria-hidden={decorative ? true : undefined}
        aria-labelledby={decorative ? undefined : titleId}
        role={decorative ? undefined : "img"}
      >
        {!decorative && <title id={titleId}>{accessibleLabel}</title>}
        <g className="q-pulse q-pulse--back">{renderRings(false)}</g>
        <g className="q-dots q-dots--back">{renderDots(false)}</g>
        <path className="q-container" d={initialPath} ref={containerRef} />
        <g className="q-glyph" ref={glyphRef}>
          {config.glyph && <Glyph name={config.glyph} />}
        </g>
        <g className="q-dots q-dots--front">{renderDots(true)}</g>
        <g className="q-pulse q-pulse--front">{renderRings(true)}</g>
      </svg>
    </span>
  );
}

export default QuiddityMark;
